import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Headphones, Users, Gift, Eye, Target, Sparkles } from 'lucide-react';

const WorkshopDetails = () => {
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '200px 0px', // Start much earlier for mobile
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const features = [
    {
      icon: <Headphones size={32} />,
      title: "חוויה באוזניות",
      description: "מסע קולי מותאם אישית שמעביר אותך לעולם של דמיון ויצירתיות",
      color: "from-dream-teal to-dream-warm-brown"
    },
    {
      icon: <Users size={32} />,
      title: "קבוצתי ואישי",
      description: "תהליך קבוצתי שמאפשר מסע אישי עמוק וייחודי לכל משתתף",
      color: "from-dream-warm-brown to-dream-teal"
    },
    {
      icon: <Gift size={32} />,
      title: "קיט אישי",
      description: "כל משתתף מקבל קיט מיוחד לבניית לוח החזון האישי שלו",
      color: "from-dream-warm-brown to-dream-pink-beige"
    },
    {
      icon: <Eye size={32} />,
      title: "ראיית העתיד",
      description: "לראות את העתיד המיטבי שלך מול העיניים ולהתרגש לקראתו",
      color: "from-dream-teal to-dream-warm-brown"
    },
    {
      icon: <Target size={32} />,
      title: "הגשמת חלומות",
      description: "כלים מעשיים להפיכת החזון למציאות עם תוכנית פעולה ברורה",
      color: "from-dream-teal to-dream-warm-brown"
    },
    {
      icon: <Sparkles size={32} />,
      title: "תפאורה קסומה",
      description: "אווירה מיוחדת עם תאורה ותפאורה שיוצרות חוויה בלתי נשכחת",
      color: "from-dream-warm-brown to-dream-teal"
    }
  ];

  const process = [
    { step: "1", title: "התכנסות", description: "יצירת אווירה מתאימה והכנת המרחב לחוויה מיוחדת" },
    { step: "2", title: "כתיבה אינטואיטיבית", description: "שחרור היצירתיות דרך כתיבה חופשית ומעמיקה" },
    { step: "3", title: "קלף עם מסר", description: "קבלת השראה והדרכה דרך מסרים אישיים" },
    { step: "4", title: "יצירת לוח חזון", description: "בניית לוח החזון האישי - ממש את החלומות לתמונה" },
    { step: "5", title: "הללויה", description: "חגיגה וחיזוק התהליך יחד עם הקבוצה" }
  ];

  return (
    <section id="workshop-details" className="section-padding bg-gradient-to-br from-dream-light-beige to-dream-soft-beige relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-64 h-64 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-gradient-to-r from-dream-pink-beige to-dream-soft-beige rounded-full blur-2xl opacity-20"></div>
      </div>

      <motion.div
        ref={ref}
        className="container-width relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            מה כולל <span className="gradient-text">המסע</span>?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            סדנה ייחודית שמשלבת טכנולוגיה, יצירתיות ועוצמה רגשית 
            למסע בלתי נשכח אל העתיד שלכם
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white p-6 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group"
            >
              <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-4 text-white group-hover:scale-110 transition-transform duration-300`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Process Section with Workshop Photo */}
        <motion.div variants={itemVariants} className="mb-16">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">
            התהליך <span className="gradient-text">שלב אחר שלב</span>
          </h3>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            <div className="relative">
              <img 
                src="/images/20241219_142337.jpg" 
                alt="תהליך הסדנה"
                className="w-full h-96 object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-black bg-opacity-20 rounded-3xl"></div>
            </div>
            
            <div className="space-y-6">
              {process.map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 mb-2">{item.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>



        {/* Target Audience */}
        <motion.div variants={itemVariants} className="text-center bg-white p-8 rounded-3xl shadow-lg">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">למי מיועדת הסדנה?</h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="p-4">
              <div className="w-16 h-16 bg-gradient-to-r from-dream-warm-brown to-dream-teal rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                <Users size={24} />
              </div>
              <h4 className="font-bold text-gray-800 mb-2">מוסדות</h4>
              <p className="text-gray-600 text-sm">עיריות, מחוזות חינוך ומוסדות ציבור</p>
            </div>
            <div className="p-4">
              <div className="w-16 h-16 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                <Users size={24} />
              </div>
              <h4 className="font-bold text-gray-800 mb-2">חברות</h4>
              <p className="text-gray-600 text-sm">ארגונים וחברות המעוניינים בפעילות העצמה</p>
            </div>
            <div className="p-4">
              <div className="w-16 h-16 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                <Users size={24} />
              </div>
              <h4 className="font-bold text-gray-800 mb-2">קבוצות פרטיות</h4>
              <p className="text-gray-600 text-sm">מינימום 20 משתתפים - מגיעות עד אליכם!</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default WorkshopDetails;
